import { z } from 'zod';
import { PARTNER_SUBJECTS } from '../areas/partner/subjects';
/**
 * CASL Action enum — shared between backend ability factory and frontend permissions
 */ export const ActionSchema = z.enum([
    'manage',
    'create',
    'read',
    'update',
    'delete',
    'readSensitive',
    /**
   * The Prozessbewertung, the per-step Variant Check and the recording
   * dashboard's survey participation rows (ADR 0021): the survey-derived half a
   * process owner and a department manager may read, granted separately from
   * `readSensitive`, which stays org-manager-only at step level.
   */ 'readProcessInsights',
    'manageStructure'
]);
/**
 * Subject types for CASL authorization
 */ export const SubjectSchema = z.enum([
    'Process',
    'System',
    'SystemStatistics',
    'OrgFunction',
    'Department',
    'Assignment',
    'Recording',
    'EntitySuggestion',
    'OrgManagerDashboard',
    'ModeratorDashboard',
    'GeneralDashboard',
    'Dashboard',
    'MyTasks',
    'Processes',
    'OrgOverview',
    'OrgSettings',
    'ActiveMonitoring',
    'Analytics',
    'Survey',
    'Bliksy',
    'Organization',
    'User',
    'WelcomePage',
    'ProcessRequest',
    'ProcessEditor',
    'Nudge',
    'Artifact',
    'ExternalContact',
    'ProcessMap',
    'ProcessChain',
    'ProcessConnection',
    'SystemLandscape',
    'ShadowSystem',
    'StepTemplate',
    'MyDepartment',
    'MyOrgFunctions',
    'MySystems',
    'OrgChart',
    'RecordingDashboard',
    'InitialSetup',
    'DataSync',
    ...PARTNER_SUBJECTS,
    'all'
]);
/**
 * Per-subject action matrix — sent in /profile response
 */ export const SubjectPermissionsSchema = z.object({
    create: z.boolean(),
    read: z.boolean(),
    update: z.boolean(),
    delete: z.boolean()
}).partial();
const subjectPermissionsFor = (subjects)=>Object.fromEntries(subjects.map((subject)=>[
            subject,
            SubjectPermissionsSchema
        ]));
/**
 * Action matrix returned in /profile response
 * Answers "can this user do this action type at all?"
 */ export const UserPermissionsSchema = z.object({
    Process: SubjectPermissionsSchema,
    ProcessEditor: SubjectPermissionsSchema,
    System: SubjectPermissionsSchema,
    OrgFunction: SubjectPermissionsSchema,
    Organization: SubjectPermissionsSchema,
    User: SubjectPermissionsSchema,
    Artifact: SubjectPermissionsSchema,
    ExternalContact: SubjectPermissionsSchema,
    Dashboard: SubjectPermissionsSchema,
    MyTasks: SubjectPermissionsSchema,
    Processes: SubjectPermissionsSchema,
    OrgOverview: SubjectPermissionsSchema,
    OrgSettings: SubjectPermissionsSchema,
    ActiveMonitoring: SubjectPermissionsSchema,
    Analytics: SubjectPermissionsSchema,
    Survey: SubjectPermissionsSchema,
    ProcessMap: SubjectPermissionsSchema,
    ProcessChain: SubjectPermissionsSchema,
    ProcessConnection: SubjectPermissionsSchema,
    SystemLandscape: SubjectPermissionsSchema,
    ShadowSystem: SubjectPermissionsSchema,
    StepTemplate: SubjectPermissionsSchema,
    EntitySuggestion: SubjectPermissionsSchema,
    OrgManagerDashboard: SubjectPermissionsSchema,
    ModeratorDashboard: SubjectPermissionsSchema,
    GeneralDashboard: SubjectPermissionsSchema,
    MyDepartment: SubjectPermissionsSchema,
    MyOrgFunctions: SubjectPermissionsSchema,
    MySystems: SubjectPermissionsSchema,
    OrgChart: SubjectPermissionsSchema,
    RecordingDashboard: SubjectPermissionsSchema,
    InitialSetup: SubjectPermissionsSchema,
    DataSync: SubjectPermissionsSchema,
    ...subjectPermissionsFor(PARTNER_SUBJECTS),
    isExternal: z.boolean()
});
/**
 * Auth context for frontend instance-level permission checks
 */ export const AuthContextSchema = z.object({
    userId: z.string(),
    managedDeptIds: z.array(z.string())
});
/**
 * Per-process capability flags sent alongside process lists
 */ export const ProcessCapabilitySchema = z.object({
    canViewSensitiveFields: z.boolean(),
    canViewProcessInsights: z.boolean(),
    canUpdate: z.boolean(),
    /** Also gates changing the process owner: both edits require ManageStructure. */ canEditDepartment: z.boolean(),
    canDelete: z.boolean(),
    canDuplicate: z.boolean()
});
/**
 * Per-system capability flags sent alongside system lists/details
 */ export const SystemCapabilitySchema = z.object({
    canViewSensitiveFields: z.boolean(),
    canUpdate: z.boolean(),
    canDelete: z.boolean()
});

//# sourceMappingURL=authorization.js.map